import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { ArrowLeft, RefreshCw, Save, Check, Shield, Database, UploadCloud, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { getMenuRoutes, getDefaultPermissions, ALL_ROLES, ROLE_LABELS, GROUP_LABELS } from '../config/routeConfig';

const AccessControl = () => {
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState({});
  const [selectedRole, setSelectedRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const menuRoutes = getMenuRoutes();
  const defaults = getDefaultPermissions();

  useEffect(() => {
    const fetchPerms = async () => {
      setLoading(true);
      try {
        const res = await api.get('/screen-permissions');
        if (res.data.exists) {
          // Merge saved permissions with defaults (new routes get defaults)
          const saved = res.data.permissions;
          const merged = { ...defaults };
          Object.keys(saved).forEach(path => { merged[path] = saved[path]; });
          setPermissions(merged);
        } else {
          setPermissions(defaults);
        }
      } catch (e) {
        setPermissions(defaults);
      }
      finally { setLoading(false); }
    };
    fetchPerms();
  }, []);

  const togglePermission = (path, role) => {
    setPermissions(prev => {
      const current = prev[path] || [];
      const updated = current.includes(role)
        ? current.filter(r => r !== role)
        : [...current, role];
      return { ...prev, [path]: updated };
    });
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/screen-permissions', { permissions });
      toast.success('Permissions saved — takes effect on next page load');
      setSaved(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const resetToDefaults = () => {
    setPermissions(defaults);
    setSaved(false);
    toast.success('Reset to defaults — save to apply');
  };

  const [migStatus, setMigStatus] = useState(null);
  const migPollRef = useRef(null);

  const startMigration = async () => {
    try {
      await api.post('/admin/migrate-images');
      toast.success('Migration started — running in background');
      migPollRef.current = setInterval(async () => {
        try {
          const res = await api.get('/admin/migrate-images/status');
          setMigStatus(res.data);
          if (res.data.complete || !res.data.running) {
            clearInterval(migPollRef.current);
            if (res.data.complete) toast.success(`Done — ${res.data.done} images migrated to cloud storage`);
          }
        } catch {}
      }, 1500);
    } catch (e) { toast.error('Failed to start migration'); }
  };

  const migPct = migStatus?.total > 0
    ? Math.round(((migStatus.done + migStatus.missing + migStatus.errors) / migStatus.total) * 100)
    : 0;

  const groupedRoutes = {};
  menuRoutes.forEach(r => {
    const g = r.group || 'other';
    if (!groupedRoutes[g]) groupedRoutes[g] = [];
    groupedRoutes[g].push(r);
  });

  const roleScreenCount = (role) => {
    return Object.values(permissions).filter(roles => roles.includes(role)).length;
  };

  if (loading) return (
    <div className="h-screen flex items-center justify-center bg-gray-50">
      <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
    </div>
  );

  return (
    <div className="h-screen flex flex-col bg-gray-50" data-testid="access-control-page">
      {/* Header */}
      <div className="bg-[#075E54] text-white px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold flex items-center gap-2">
                <Shield className="w-5 h-5" /> Access Control
              </h1>
              <p className="text-xs text-emerald-200">Manage screen visibility per role</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" className="text-white hover:bg-emerald-700 h-8 text-xs" onClick={resetToDefaults}>
              Reset
            </Button>
            <Button size="sm" className="bg-white text-emerald-700 hover:bg-emerald-50 h-8 text-xs" onClick={handleSave} disabled={saving} data-testid="save-perms-btn">
              {saving ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : saved ? <Check className="w-3.5 h-3.5 mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              {saved ? 'Saved' : 'Save'}
            </Button>
          </div>
        </div>
      </div>

      {/* Role Selector */}
      <div className="px-4 pt-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Select Role</p>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {ALL_ROLES.map(role => (
            <button
              key={role}
              onClick={() => setSelectedRole(selectedRole === role ? '' : role)}
              className={`flex-shrink-0 px-3 py-2 rounded-lg text-xs font-semibold border transition-all ${
                selectedRole === role
                  ? 'bg-emerald-600 text-white border-emerald-600 shadow-md'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
              }`}
              data-testid={`role-btn-${role}`}
            >
              {ROLE_LABELS[role] || role}
              <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] ${
                selectedRole === role ? 'bg-white/20' : 'bg-gray-100'
              }`}>{roleScreenCount(role)}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Screen Toggles */}
      <div className="flex-1 overflow-y-auto px-4 pt-3 pb-4">
        {!selectedRole ? (
          <div className="text-center py-12 text-gray-400">
            <Shield className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p className="font-medium">Select a role above</p>
            <p className="text-sm mt-1">Then toggle which screens that role can access</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold text-gray-800">{ROLE_LABELS[selectedRole]}</p>
              <p className="text-xs text-gray-400">{roleScreenCount(selectedRole)} screens enabled</p>
            </div>

            {Object.entries(groupedRoutes).map(([group, routes]) => (
              <div key={group}>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 mt-3">
                  {GROUP_LABELS[group] || group}
                </p>
                <div className="bg-white rounded-lg border divide-y">
                  {routes.map(route => {
                    const isEnabled = (permissions[route.path] || []).includes(selectedRole);
                    const isDefault = (route.defaultRoles || []).includes(selectedRole);
                    return (
                      <div key={route.path} className="flex items-center justify-between px-4 py-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">{route.label}</p>
                          <p className="text-[10px] text-gray-400">{route.path}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {!isDefault && isEnabled && (
                            <span className="text-[9px] text-blue-500 font-medium">custom</span>
                          )}
                          <button
                            onClick={() => togglePermission(route.path, selectedRole)}
                            className={`relative w-11 h-6 rounded-full transition-colors ${
                              isEnabled ? 'bg-emerald-500' : 'bg-gray-300'
                            }`}
                            data-testid={`toggle-${route.path}-${selectedRole}`}
                          >
                            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                              isEnabled ? 'translate-x-5.5 left-[1px]' : 'translate-x-0 left-[2px]'
                            }`}
                              style={{ transform: isEnabled ? 'translateX(22px)' : 'translateX(0)' }}
                            />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Image Migration Panel */}
        <div className="m-4 rounded-xl border border-blue-100 bg-blue-50 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Database className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-blue-900 text-sm">Migrate Images to Cloud Storage</h3>
          </div>
          <p className="text-xs text-blue-700 mb-3">
            Uploads all existing product images to persistent cloud storage so they survive server restarts.
            Safe to run multiple times.
          </p>
          {migStatus && (
            <div className="mb-3">
              <div className="flex justify-between text-xs text-blue-800 mb-1">
                <span>{migStatus.complete ? 'Complete' : migStatus.running ? 'Migrating...' : 'Idle'}</span>
                <span>{migStatus.done}/{migStatus.total} migrated</span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div className="bg-blue-600 h-2 rounded-full transition-all duration-500" style={{ width: `${migPct}%` }} />
              </div>
              {migStatus.complete && (
                <div className="flex gap-3 mt-2 text-xs">
                  <span className="text-green-700 font-medium flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />{migStatus.done} migrated</span>
                  {migStatus.missing > 0 && <span className="text-amber-600">{migStatus.missing} not found (already gone)</span>}
                  {migStatus.errors > 0 && <span className="text-red-600">{migStatus.errors} errors</span>}
                </div>
              )}
            </div>
          )}
          <Button
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white h-8 text-xs"
            onClick={startMigration}
            disabled={migStatus?.running}
            data-testid="migrate-images-btn"
          >
            {migStatus?.running
              ? <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />Migrating...</>
              : migStatus?.complete
              ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />Run Again</>
              : <><UploadCloud className="w-3.5 h-3.5 mr-1.5" />Start Migration</>
            }
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AccessControl;
